import { useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';

export const usePolling = (
  refetchFunction,
  interval = 15000,
  paused = false
) => {
  const { isPollingEnabled, setRefreshing, updateTimeUntilRefresh } = useApp();
  const timerRef = useRef(null);
  const timeLeftRef = useRef(interval / 1000);

  // Single timer approach that handles both countdown and polling
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) clearInterval(timerRef.current);

    if (!isPollingEnabled || !refetchFunction || paused) {
      return;
    }

    // Initialize timer
    timeLeftRef.current = interval / 1000;
    updateTimeUntilRefresh(timeLeftRef.current);

    // Create single timer that ticks every second
    timerRef.current = setInterval(async () => {
      timeLeftRef.current -= 1;
      updateTimeUntilRefresh(timeLeftRef.current);

      // When countdown reaches 0, trigger refresh and reset
      if (timeLeftRef.current <= 0) {
        try {
          setRefreshing(true);
          await refetchFunction();
        } catch (error) {
          console.error('Polling error:', error);
        } finally {
          setRefreshing(false);
        }

        // Reset countdown
        timeLeftRef.current = interval / 1000;
        updateTimeUntilRefresh(timeLeftRef.current);
      }
    }, 1000);

    // Cleanup function
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [
    isPollingEnabled,
    refetchFunction,
    interval,
    paused,
    setRefreshing,
    updateTimeUntilRefresh,
  ]);

  const manualRefresh = async () => {
    try {
      setRefreshing(true);
      await refetchFunction();
    } catch (error) {
      console.error('Manual refresh error:', error);
    } finally {
      setRefreshing(false);
    }

    // Reset countdown after manual refresh
    if (isPollingEnabled) {
      timeLeftRef.current = interval / 1000;
      updateTimeUntilRefresh(timeLeftRef.current);
    }
  };

  return { manualRefresh };
};
